(() => {
  if (!customElements.get('oss-cart-upsell')) {
    customElements.define('oss-cart-upsell', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.loaded === 'true') return;
        this.dataset.loaded = 'true';
        this.index = 0;
        this.load();
      }

      async load() {
        const url = this.dataset.url;
        const target = this.querySelector('[data-upsell-content]');
        if (!url || !target) return;
        try {
          const response = await fetch(url, { credentials: 'same-origin' });
          if (!response.ok) throw new Error('Could not load cart recommendations');
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const source = doc.querySelector('#OssCartUpsellContent');
          if (!source || !source.querySelector('[data-upsell-card]')) {
            this.hidden = true;
            return;
          }
          target.innerHTML = source.innerHTML;
          this.cards = Array.from(this.querySelectorAll('[data-upsell-card]'));
          this.render();

          const heading = this.querySelector('.oss-cart-upsell__arrows');
          if (heading && this.cards.length > 1) {
            heading.innerHTML = '<button class="oss-cart-upsell__nav" type="button" data-upsell-prev aria-label="Previous recommendation">‹</button><button class="oss-cart-upsell__nav" type="button" data-upsell-next aria-label="Next recommendation">›</button>';
            heading.querySelector('[data-upsell-prev]').addEventListener('click', () => { this.index = (this.index - 1 + this.cards.length) % this.cards.length; this.render(); });
            heading.querySelector('[data-upsell-next]').addEventListener('click', () => { this.index = (this.index + 1) % this.cards.length; this.render(); });
          } else if (heading) {
            heading.style.visibility = 'hidden';
          }
        } catch (error) {
          console.warn(error);
          this.hidden = true;
        }
      }

      render() {
        if (!this.cards) return;
        this.cards.forEach((card, i) => card.hidden = i !== this.index);
      }
    });
  }

  if (!customElements.get('oss-cart-tools')) {
    customElements.define('oss-cart-tools', class extends HTMLElement {
      connectedCallback() {
        if (this.dataset.ready === 'true') return;
        this.dataset.ready = 'true';
        this.panel = this.querySelector('[data-cart-tool-panel]');
        this.content = this.querySelector('[data-cart-tool-content]');
        this.querySelectorAll('[data-cart-tool]').forEach(btn => btn.addEventListener('click', () => this.open(btn.dataset.cartTool)));
        this.querySelector('[data-cart-tool-close]')?.addEventListener('click', () => this.close());
      }

      close() {
        if (this.panel) this.panel.hidden = true;
      }

      open(type) {
        if (!this.panel || !this.content) return;
        this.panel.hidden = false;
        if (type === 'discount') this.renderDiscount();
        if (type === 'shipping') this.renderShipping();
        if (type === 'share') this.renderShare();
      }

      renderDiscount() {
        this.content.innerHTML = `
          <p class="oss-cart-tool-panel__title">Discount</p>
          <form class="oss-cart-tool-form" data-discount-form>
            <input type="text" autocomplete="off" placeholder="Discount code" aria-label="Discount code">
            <button type="submit">Apply</button>
          </form>
          <div class="oss-cart-tool-result" data-tool-result></div>`;
        const form = this.content.querySelector('[data-discount-form]');
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const code = form.querySelector('input').value.trim();
          const result = this.content.querySelector('[data-tool-result]');
          if (!code) return;
          result.textContent = 'Applying…';
          try {
            const url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
            const response = await fetch(url, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                discount: code,
                sections: ['cart-drawer', 'cart-icon-bubble'],
                sections_url: window.location.pathname
              })
            });
            const state = await response.json();
            if (!response.ok || state.status) throw new Error(state.description || 'Discount code could not be applied.');
            const drawer = document.querySelector('cart-drawer');
            if (state.sections && drawer && typeof drawer.renderContents === 'function') drawer.renderContents(state);
            else window.location.reload();
          } catch (error) {
            result.textContent = error.message || 'Discount code could not be applied.';
          }
        });
      }

      renderShipping() {
        this.content.innerHTML = `
          <p class="oss-cart-tool-panel__title">Estimate shipping</p>
          <form class="oss-cart-tool-form" data-shipping-form>
            <input type="text" autocomplete="postal-code" placeholder="UK postcode" aria-label="UK postcode">
            <button type="submit">Estimate</button>
          </form>
          <div class="oss-cart-tool-result" data-tool-result></div>`;
        const form = this.content.querySelector('[data-shipping-form]');
        form.addEventListener('submit', async (event) => {
          event.preventDefault();
          const postcode = form.querySelector('input').value.trim();
          const result = this.content.querySelector('[data-tool-result]');
          if (!postcode) return;
          result.textContent = 'Checking rates…';
          try {
            const query = new URLSearchParams({
              'shipping_address[country]': 'United Kingdom',
              'shipping_address[zip]': postcode
            });
            const response = await fetch(`/cart/shipping_rates.json?${query.toString()}`, { credentials: 'same-origin' });
            const data = await response.json();
            if (!response.ok || data.error) throw new Error(typeof data.error === 'string' ? data.error : 'Unable to calculate shipping.');
            const rates = data.shipping_rates || [];
            result.innerHTML = rates.length
              ? rates.map(rate => `<div>${this.escape(rate.name)} — <strong>${this.escape(rate.price)} ${this.escape(rate.currency || '')}</strong></div>`).join('')
              : 'No shipping rates were returned for that postcode.';
          } catch (error) {
            result.textContent = error.message || 'Unable to calculate shipping.';
          }
        });
      }

      async renderShare() {
        this.content.innerHTML = '<p class="oss-cart-tool-panel__title">Share cart</p><div class="oss-cart-tool-result">Creating link…</div>';
        const result = this.content.querySelector('.oss-cart-tool-result');
        try {
          const response = await fetch('/cart.js', { credentials: 'same-origin' });
          const cart = await response.json();
          const items = (cart.items || []).map(item => `${item.variant_id}:${item.quantity}`).join(',');
          const shareUrl = `${window.location.origin}/cart/${items}`;
          this.content.innerHTML = `
            <p class="oss-cart-tool-panel__title">Share cart</p>
            <div class="oss-cart-tool-form">
              <input type="text" readonly value="${this.escapeAttr(shareUrl)}" aria-label="Cart share link">
              <button type="button" data-copy-cart>Copy</button>
            </div>
            <div class="oss-cart-tool-result" data-tool-result></div>`;
          this.content.querySelector('[data-copy-cart]').addEventListener('click', async () => {
            const status = this.content.querySelector('[data-tool-result]');
            try {
              await navigator.clipboard.writeText(shareUrl);
              status.textContent = 'Cart link copied.';
            } catch (_) {
              const input = this.content.querySelector('input');
              input.select();
              document.execCommand('copy');
              status.textContent = 'Cart link copied.';
            }
          });
        } catch (error) {
          result.textContent = 'Unable to create a share link right now.';
        }
      }

      escape(value) {
        return String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#039;'}[c]));
      }
      escapeAttr(value) { return this.escape(value); }
    });
  }
})();
